import { LayerNames, LayerVisibilityMode } from '@/modules/common/types/layers';
import { FDDM_TAG, Tag } from '@/modules/floorplanDesign/constants';
import { UserPreferenceName } from '@/modules/userPreferences';

export type LayerDefinition = {
  name: LayerNames;
  labelKey: string;
  fallbackLabel: string;
  fddmTags: readonly Tag[];
  visibilityModePreference?: UserPreferenceName;
  lockable: boolean;
  lod?: {
    showFromZoomPreference?: UserPreferenceName;
    hideFromZoomPreference?: UserPreferenceName;
  };
  visibilityMode?: LayerVisibilityMode;
  children?: readonly LayerTreeItem[];
};

type VirtualLayerTreeItem = {
  id: string;
  labelKey: string;
  fallbackLabel: string;
  lockable: boolean;
  children: readonly LayerTreeItem[];
};

export type LayerTreeItem = LayerDefinition | VirtualLayerTreeItem;

export const isLayerDefinition = (item: LayerTreeItem): item is LayerDefinition => 'name' in item;

// This should be the single source of truth regarding everything with layers.
export const FDDM_LAYER_TREE = [
  {
    name: LayerNames.GENERATED_STATIONS,
    fddmTags: [FDDM_TAG.STATION],
    labelKey: 'layers.stations',
    fallbackLabel: 'Stations',
    visibilityModePreference: UserPreferenceName.LAYER_GENERATED_STATIONS_VISIBILITY_MODE,
    lod: {
      showFromZoomPreference: UserPreferenceName.LOD_BOXES_ZOOM_THRESHOLD,
    },
    lockable: false,
  },
  {
    name: LayerNames.GENERATED_POIS,
    fddmTags: [FDDM_TAG.POINT_OF_INTEREST, FDDM_TAG.POSE],
    labelKey: 'layers.pois',
    fallbackLabel: 'POIs',
    visibilityModePreference: UserPreferenceName.LAYER_GENERATED_POIS_VISIBILITY_MODE,
    lod: {
      showFromZoomPreference: UserPreferenceName.LOD_BOXES_ZOOM_THRESHOLD,
    },
    lockable: false,
  },
  {
    name: LayerNames.VEHICLE_LAYOUT_POI_CONNECTIONS,
    fddmTags: [FDDM_TAG.VEHICLE_LAYOUT_POI_CONNECTION],
    labelKey: 'layers.vehicle_layout_poi_connections',
    fallbackLabel: 'POI Connections',
    visibilityModePreference: UserPreferenceName.LAYER_GENERATED_POI_CONNECTIONS_VISIBILITY_MODE,
    lod: {
      showFromZoomPreference: UserPreferenceName.LOD_VEHICLE_LAYOUTS_ZOOM_THRESHOLD,
      hideFromZoomPreference: UserPreferenceName.LOD_TRACKS_ZOOM_THRESHOLD,
    },
    lockable: false,
  },
  {
    name: LayerNames.TRACKS,
    fddmTags: [FDDM_TAG.TRACK_PIECE],
    labelKey: 'layers.track_pieces',
    fallbackLabel: 'Tracks',
    visibilityModePreference: UserPreferenceName.LAYER_GENERATED_TRACKS_VISIBILITY_MODE,
    lod: {
      showFromZoomPreference: UserPreferenceName.LOD_TRACKS_ZOOM_THRESHOLD,
    },
    lockable: false,
  },
  {
    name: LayerNames.SWEPT_PATHS,
    fddmTags: [FDDM_TAG.SWEPT_PATH],
    labelKey: 'layers.swept_paths',
    fallbackLabel: 'Swept Paths',
    visibilityModePreference: UserPreferenceName.LAYER_GENERATED_SWEPT_PATHS_VISIBILITY_MODE,
    lod: {
      showFromZoomPreference: UserPreferenceName.LOD_TRACKS_ZOOM_THRESHOLD,
    },
    lockable: false,
  },
  {
    name: LayerNames.FLOWS,
    fddmTags: [FDDM_TAG.FLOW, FDDM_TAG.FLOW_STOP, FDDM_TAG.LAYER_FLOWS],
    labelKey: 'layers.flows',
    fallbackLabel: 'Flows',
    visibilityModePreference: UserPreferenceName.LAYER_FLOWS_VISIBILITY_MODE,
    lockable: false,
  },
  {
    name: LayerNames.CONNECTIONS,
    fddmTags: [
      FDDM_TAG.INTER_AREA_CONNECTION,
      FDDM_TAG.A_TO_B_CONNECTION,
      FDDM_TAG.B_TO_A_CONNECTION,
      FDDM_TAG.CROSSING,
      FDDM_TAG.DISTANT_CONNECTION,
      FDDM_TAG.BUBBLE,
      FDDM_TAG.BUBBLE_ARROW,
      FDDM_TAG.BUBBLE_DIVIDER,
      FDDM_TAG.LAYER_CONNECTIONS,
      FDDM_TAG.LAYER_CONNECTION_BUBBLES,
    ],
    labelKey: 'layers.area_connections',
    fallbackLabel: 'Area connections',
    visibilityModePreference: UserPreferenceName.LAYER_CONNECTIONS_VISIBILITY_MODE,
    lod: {
      showFromZoomPreference: UserPreferenceName.LOD_BUBBLES_ZOOM_THRESHOLD,
      hideFromZoomPreference: UserPreferenceName.LOD_TRACKS_ZOOM_THRESHOLD,
    },
    lockable: false,
  },
  {
    id: 'interior',
    labelKey: 'layers.interior',
    fallbackLabel: 'Interior',
    lockable: true,
    children: [
      {
        name: LayerNames.WALLS,
        fddmTags: [FDDM_TAG.WALL, FDDM_TAG.LAYER_WALLS],
        labelKey: 'layers.walls',
        fallbackLabel: 'Walls',
        visibilityModePreference: UserPreferenceName.LAYER_WALLS_VISIBILITY_MODE,
        lockable: true,
      },
      {
        name: LayerNames.OBSTACLES,
        fddmTags: [FDDM_TAG.OBSTACLE, FDDM_TAG.LAYER_OBSTACLES],
        labelKey: 'layers.obstacles',
        fallbackLabel: 'Obstacles',
        visibilityModePreference: UserPreferenceName.LAYER_OBSTACLES_VISIBILITY_MODE,
        lockable: true,
      },
    ],
  },
  {
    id: 'areas',
    labelKey: 'layers.areas',
    fallbackLabel: 'Areas',
    lockable: true,
    children: [
      {
        id: 'roads',
        labelKey: 'layers.highways',
        fallbackLabel: 'Highways',
        lockable: true,
        children: [
          {
            name: LayerNames.HIGHWAYS,
            fddmTags: [FDDM_TAG.LAYER_HIGHWAYS],
            labelKey: 'layers.highways',
            fallbackLabel: 'Highways',
            visibilityModePreference: UserPreferenceName.LAYER_HIGHWAYS_VISIBILITY_MODE,
            lockable: true,
          },
          {
            name: LayerNames.ANGLED_HIGHWAYS,
            fddmTags: [FDDM_TAG.LAYER_ANGLED_HIGHWAYS],
            labelKey: 'layers.angled_highways',
            fallbackLabel: 'Angled Highways',
            visibilityModePreference: UserPreferenceName.LAYER_ANGLED_HIGHWAYS_VISIBILITY_MODE,
            lockable: true,
          },
        ],
      },
      {
        name: LayerNames.INTAKE,
        fddmTags: [FDDM_TAG.LAYER_INTAKE],
        labelKey: 'layers.intake',
        fallbackLabel: 'Intake',
        visibilityModePreference: UserPreferenceName.LAYER_INTAKE_VISIBILITY_MODE,
        lockable: true,
      },
      {
        name: LayerNames.DELIVERY,
        fddmTags: [FDDM_TAG.LAYER_DELIVERY],
        labelKey: 'layers.delivery',
        fallbackLabel: 'Delivery',
        visibilityModePreference: UserPreferenceName.LAYER_DELIVERY_VISIBILITY_MODE,
        lockable: true,
      },
      {
        name: LayerNames.STORAGE,
        fddmTags: [FDDM_TAG.LAYER_STORAGE],
        labelKey: 'layers.storage',
        fallbackLabel: 'Storage',
        visibilityModePreference: UserPreferenceName.LAYER_STORAGE_VISIBILITY_MODE,
        lockable: true,
      },
      {
        name: LayerNames.CHARGING,
        fddmTags: [FDDM_TAG.LAYER_CHARGING],
        labelKey: 'layers.charging',
        fallbackLabel: 'Charging',
        visibilityModePreference: UserPreferenceName.LAYER_CHARGING_VISIBILITY_MODE,
        lockable: true,
      },
      {
        name: LayerNames.PARKING,
        fddmTags: [FDDM_TAG.LAYER_PARKING],
        labelKey: 'layers.parking',
        fallbackLabel: 'Parking',
        visibilityModePreference: UserPreferenceName.LAYER_PARKING_VISIBILITY_MODE,
        lockable: true,
      },
      {
        name: LayerNames.MANUAL,
        fddmTags: [FDDM_TAG.LAYER_MANUAL],
        labelKey: 'layers.manual',
        fallbackLabel: 'Manual',
        visibilityModePreference: UserPreferenceName.LAYER_MANUAL_VISIBILITY_MODE,
        lockable: true,
      },
      {
        name: LayerNames.EXTRACTION,
        fddmTags: [FDDM_TAG.LAYER_EXTRACTION],
        labelKey: 'layers.extraction',
        fallbackLabel: 'Extraction',
        visibilityModePreference: UserPreferenceName.LAYER_EXTRACTION_VISIBILITY_MODE,
        lockable: true,
      },
      {
        name: LayerNames.INSERTION,
        fddmTags: [FDDM_TAG.LAYER_INSERTION],
        labelKey: 'layers.insertion',
        fallbackLabel: 'Insertion',
        visibilityModePreference: UserPreferenceName.LAYER_INSERTION_VISIBILITY_MODE,
        lockable: true,
      },
      {
        name: LayerNames.PROCESS,
        fddmTags: [FDDM_TAG.LAYER_PROCESS],
        labelKey: 'layers.process',
        fallbackLabel: 'Process',
        visibilityModePreference: UserPreferenceName.LAYER_PROCESS_VISIBILITY_MODE,
        lockable: true,
      },
    ],
  },
  {
    name: LayerNames.REFERENCE,
    fddmTags: [
      FDDM_TAG.REFERENCE_IMAGE,
      FDDM_TAG.REFERENCE_SCALING_LINE,
      FDDM_TAG.REFERENCE_SCALING_POINT_CROSS_LINE_A,
      FDDM_TAG.REFERENCE_SCALING_POINT_CROSS_LINE_B,
      FDDM_TAG.LAYER_REFERENCE,
    ],
    labelKey: 'layers.reference',
    fallbackLabel: 'Reference',
    visibilityModePreference: UserPreferenceName.LAYER_REFERENCES_VISIBILITY_MODE,
    lockable: false,
  },
  {
    name: LayerNames.LOGICAL_SPACES,
    fddmTags: [
      FDDM_TAG.LOGICAL_SPACE,
      FDDM_TAG.LOGICAL_SPACE_TRAFFIC_BOARD,
      FDDM_TAG.LOGICAL_SPACE_TRAFFIC_BOARD_TEXT,
      FDDM_TAG.LAYER_LOGICAL_SPACES,
    ],
    labelKey: 'layers.logical_spaces',
    fallbackLabel: 'Logical spaces',
    visibilityModePreference: UserPreferenceName.LAYER_LOGICAL_SPACES_VISIBILITY_MODE,
    lockable: true,
  },
  {
    name: LayerNames.GROUPS,
    fddmTags: [FDDM_TAG.GROUP, FDDM_TAG.LAYER_GROUPS],
    labelKey: 'layers.groups',
    fallbackLabel: 'Groups',
    visibilityModePreference: UserPreferenceName.LAYER_GROUPS_VISIBILITY_MODE,
    lockable: false,
  },
  {
    name: LayerNames.ORIGIN,
    fddmTags: [FDDM_TAG.ORIGIN],
    labelKey: 'layers.origin',
    fallbackLabel: 'Origin',
    lockable: false,
  },
] as const satisfies readonly LayerTreeItem[];

export const getLayerTreeItemKey = (item: LayerTreeItem): string => {
  if (isLayerDefinition(item)) return item.name;

  return item.id;
};

export const flattenLayerTree = (items: readonly LayerTreeItem[]): LayerTreeItem[] =>
  items.flatMap((item) => [item, ...flattenLayerTree(item.children ?? [])]);

export const FDDM_LAYER_TREE_ITEMS = flattenLayerTree(FDDM_LAYER_TREE);

export const FDDM_LAYER_DEFINITIONS = FDDM_LAYER_TREE_ITEMS.filter(isLayerDefinition);

export const FDDM_LAYER_NAMES = FDDM_LAYER_DEFINITIONS.map((definition) => definition.name);

export const getLayerDefinition = (layer: LayerNames): LayerDefinition | undefined =>
  FDDM_LAYER_DEFINITIONS.find((definition) => definition.name === layer);

export const getLayerVisibilityModePreference = (
  layer: LayerNames,
): UserPreferenceName | undefined => getLayerDefinition(layer)?.visibilityModePreference;

export const getLayerPreference = getLayerVisibilityModePreference;

export const isLayerLockable = (layer: LayerNames): boolean =>
  getLayerDefinition(layer)?.lockable === true;

export const getLayerDefinitionsForTag = (tag: Tag): LayerDefinition[] =>
  FDDM_LAYER_DEFINITIONS.filter((definition) => definition.fddmTags.includes(tag));
